/**
 * Leave Balances list — Session 3a.5 PR 4 closes the audit gap.
 *
 * Original R1 shape: placeholder EmptyState linking out. The audit
 * flagged this as ❌ (no visible Create/Edit/Delete). Per §4.3 the
 * fix adds:
 *   - Visible '+ Adjust Balance' primary CTA → /operations/leave/adjustments
 *     (the Session 3a bespoke adjustment page that already wires the
 *     `leave_balance_events` audit trail).
 *   - InlineAISuggestion (Tashghil) explaining the balance flow.
 *   - Two row-pattern shortcut cards (View employee balance · Open
 *     calendar) so the user has somewhere to go from this list-less
 *     page.
 *
 * Full per-employee DataTable integration with live balance data
 * (the R2 follow-up flagged at original R1 ship) remains deferred
 * pending the M15 retrofit; this PR ensures the CRUD action surface
 * is visible TODAY.
 */

import { CalendarClock, Plus, Sparkles, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  EmptyState,
  InlineAISuggestion,
  OperationsReportsButton,
  PageHeader,
} from "../../../components/ui";
import { HrInitiateLink } from "../../../components/hr_operations";

export function LeaveBalancesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("leave.section_balance")}
        breadcrumbs={[
          { label: t("nav.operations"), href: "/operations" },
          { label: t("nav.leave"), href: "/leave/requests" },
          { label: t("leave.section_balance") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <HrInitiateLink transactionType="leave" />
            <OperationsReportsButton
              domain="leaves"
              domainLabel={t("nav.leave", "Leaves")}
            />
          </div>
        }
      />

      {/* §4.3 — Primary CTA per Session 3a.5 audit fix */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-text-tertiary">
          {t(
            "leave.balances.hint",
            "Balances accrue per leave_type policy (M15.3). Adjustments leave an audit row in leave_balance_events.",
          )}
        </p>
        <button
          type="button"
          onClick={() => navigate("/operations/leave/adjustments")}
          className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
        >
          <Plus className="h-4 w-4" />
          {t("leave.balances.cta.adjust", "Adjust Balance")}
        </button>
      </div>

      <InlineAISuggestion
        agent="tashghil"
        icon={<Sparkles className="h-4 w-4" />}
        severity="info"
        headline={t(
          "leave.balances.suggestion.headline",
          "Year-end forecast: at current accrual rates, several balances will hit max by Dec 31.",
        )}
        body={t(
          "leave.balances.suggestion.body",
          "Tashghil suggests planning encashment runs for the next cycle. Open Encashment to start a batch run.",
        )}
        primaryAction={{
          label: t("leave.balances.suggestion.encashment", "Open Encashment"),
          onClick: () => navigate("/operations/leave/encashment"),
        }}
        suggestionKey="leave-balances-forecast"
      />

      {/* Shortcut grid — gives HR somewhere to act from. The full per-
          employee DataTable lands in the M15 retrofit. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <button
          type="button"
          onClick={() => navigate("/people/employees")}
          className="flex items-start gap-3 rounded-2xl border border-border-subtle bg-white p-4 text-start hover:border-brand-blue hover:shadow-sm"
        >
          <span className="rounded-lg bg-brand-blue/10 p-2 text-[#2260C9]">
            <Users className="h-5 w-5" />
          </span>
          <span className="flex-1">
            <span className="block font-medium text-text-primary">
              {t(
                "leave.balances.shortcut.employee.title",
                "View an employee's balance",
              )}
            </span>
            <span className="mt-1 block text-xs text-text-secondary">
              {t(
                "leave.balances.shortcut.employee.body",
                "Open the master file → Leave tab to see accrual + history per leave type.",
              )}
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => navigate("/operations/leave/calendar")}
          className="flex items-start gap-3 rounded-2xl border border-border-subtle bg-white p-4 text-start hover:border-brand-blue hover:shadow-sm"
        >
          <span className="rounded-lg bg-brand-blue/10 p-2 text-[#2260C9]">
            <CalendarClock className="h-5 w-5" />
          </span>
          <span className="flex-1">
            <span className="block font-medium text-text-primary">
              {t(
                "leave.balances.shortcut.calendar.title",
                "Open leave calendar",
              )}
            </span>
            <span className="mt-1 block text-xs text-text-secondary">
              {t(
                "leave.balances.shortcut.calendar.body",
                "Month grid view — useful for spotting upcoming leave that will deplete balances.",
              )}
            </span>
          </span>
        </button>
      </div>

      <EmptyState
        icon={CalendarClock}
        headline={t("leave.section_balance")}
        description={t("operations.leave_balances_placeholder")}
        action={{
          label: t("leave.balances.cta.adjust", "Adjust Balance"),
          onClick: () => navigate("/operations/leave/adjustments"),
        }}
      />
    </div>
  );
}
